import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { useState } from "react";
import { ProposalActionButton } from "work/proposals/_views/_shared/proposal-styled-components";

export const ACCEPT_ALL_LABEL = "Accept All";
export const REJECT_ALL_LABEL = "Reject All";
export const MORE_DECISIONS_LABEL = "More change decisions";
// The tooltip names what is behind the arrow rather than describing the arrow: the one thing a split
// button costs over two side-by-side buttons is that Reject All is no longer on screen to be found.
export const MORE_DECISIONS_TOOLTIP = "More decisions, including Reject All";

const SplitButtonContainer = styled("span")(({ theme }) => ({
  flexShrink: 0,
  maxWidth: "22rem",
  // As wide as its label on a comfortable pane, like every action in the row (#5973).
  minWidth: theme.spacing(15),
  width: "auto",
  [theme.breakpoints.down("md")]: {
    maxWidth: "none",
    minWidth: 0,
  },
}));
// The primary half carries the group's whole width less the toggle, so "Accept All" keeps reading as
// one button rather than as a button that happens to sit next to an arrow.
const PrimaryDecisionButton = styled(ProposalActionButton)(() => ({
  flex: 1,
  minWidth: 0,
}));
const DecisionMenuToggle = styled(Button)(({ theme }) => ({
  flex: "0 0 auto",
  minWidth: theme.spacing(4.5),
  paddingLeft: 0,
  paddingRight: 0,
}));
// Rejecting every incoming change is the destructive half of the pair, and it says so in the menu the
// same way it used to say so as a standalone red button.
const RejectAllMenuItem = styled(MenuItem)(({ theme }) => ({
  color: theme.palette.error.main,
  "& .MuiListItemIcon-root": {
    color: "inherit",
    minWidth: theme.spacing(4),
  },
}));

type Props = {
  className?: string;
  size?: "small" | "medium";
  acceptDisabled?: boolean;
  rejectDisabled?: boolean;
  onAcceptAll: () => void;
  onRejectAll: () => void;
};

/**
 * The whole-proposal bulk decision, as one split button: accepting every incoming change is the
 * primary half, and rejecting every incoming change lives one deliberate click further away in the
 * menu.
 *
 * #4963: the two used to sit side by side as equally weighted outlined buttons, so the destructive one
 * was exactly as easy to hit as the constructive one, and neither read as the action a viewer resolving
 * a counterparty's submission normally wants. Both decisions still run through the page's own
 * confirmation, so the menu is about which decision is being proposed, not about skipping the check.
 */
export default function BulkChangeDecisionButton({
  className,
  size,
  acceptDisabled,
  rejectDisabled,
  onAcceptAll,
  onRejectAll,
}: Readonly<Props>) {
  // The group element is held in state rather than a ref: the menu needs it while rendering, and a
  // ref read during render both misses the first paint and bails the whole component out of the
  // React Compiler.
  const [groupElement, setGroupElement] = useState<HTMLDivElement | null>(null);
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  function handleAcceptAll() {
    setMenuIsOpen(false);
    onAcceptAll();
  }

  function handleRejectAll() {
    setMenuIsOpen(false);
    onRejectAll();
  }

  return (
    <SplitButtonContainer className={className}>
      <ButtonGroup
        disableElevation
        variant="outlined"
        color="success"
        size={size}
        ref={setGroupElement}
        aria-label="Bulk change decisions"
        sx={{ display: "flex", width: "100%" }}
      >
        <Tooltip enterDelay={600} placement="bottom" title="Accept all changes">
          <span style={{ display: "flex", flex: 1, minWidth: 0 }}>
            <PrimaryDecisionButton
              variant="outlined"
              color="success"
              size={size}
              startIcon={<DoneAllIcon />}
              loading={false}
              disabled={acceptDisabled}
              onClick={handleAcceptAll}
            >
              {ACCEPT_ALL_LABEL}
            </PrimaryDecisionButton>
          </span>
        </Tooltip>
        {/* describeChild, so the tooltip describes the toggle rather than renaming it: MUI's default
            writes the title over the child's own aria-label, which is this toggle's accessible name. */}
        <Tooltip describeChild enterDelay={600} placement="bottom" title={MORE_DECISIONS_TOOLTIP}>
          {/* A disabled button receives no hover events, so the tooltip hangs on a wrapper the way the
              rest of this toolbar does it. */}
          <span style={{ display: "flex" }}>
            <DecisionMenuToggle
              color="success"
              size={size}
              disabled={rejectDisabled}
              aria-label={MORE_DECISIONS_LABEL}
              aria-haspopup="menu"
              aria-expanded={menuIsOpen}
              aria-controls={menuIsOpen ? "bulk-change-decision-menu" : undefined}
              onClick={() => setMenuIsOpen((isOpen) => !isOpen)}
            >
              <ArrowDropDownIcon />
            </DecisionMenuToggle>
          </span>
        </Tooltip>
      </ButtonGroup>
      <Menu
        id="bulk-change-decision-menu"
        anchorEl={groupElement}
        open={menuIsOpen}
        onClose={() => setMenuIsOpen(false)}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        slotProps={{ list: { "aria-label": "Bulk change decisions" } }}
      >
        <RejectAllMenuItem onClick={handleRejectAll}>
          <ListItemIcon>
            <CloseIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{REJECT_ALL_LABEL}</ListItemText>
        </RejectAllMenuItem>
      </Menu>
    </SplitButtonContainer>
  );
}
